import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRight, User, Clock, Search } from "lucide-react";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { getToken } from "@/lib/auth";
import { listFiles, createFile, deleteFile, getReminders } from "@/lib/ficheApi";
import { FICHE_T, emptyFiche } from "@/data/fiche";

export default function PatientsList() {
  const navigate = useNavigate();
  const [lang, setLang] = useState("fr");
  const [files, setFiles] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const T = FICHE_T[lang];

  const load = () => listFiles().then((d) => { setFiles(d); setLoading(false); }).catch(() => { setLoading(false); });

  useEffect(() => {
    if (!getToken()) { navigate("/praticien"); return; }
    load();
    getReminders().then(setReminders).catch(() => {});
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setCreating(true);
    try {
      const f = await createFile({ nom: "", prenom: "", data: emptyFiche() });
      navigate(`/praticien/patients/${f.id}`);
    } catch {
      toast.error(T.saveError);
      setCreating(false);
    }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await deleteFile(id);
      setFiles((prev) => prev.filter((f) => f.id !== id));
    } catch {
      toast.error(T.saveError);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 no-print">
        <button onClick={() => navigate("/praticien/tableau-de-bord")} data-testid="patients-back-dashboard" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{T.backDashboard}</button>
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>

      <div className="text-center mb-8 animate-fade-up">
        <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">{T.pageTitle}</h1>
        <p className="text-[var(--ink-soft)] max-w-xl mx-auto leading-relaxed">{T.pageIntro}</p>
      </div>

      <div className="flex justify-center mb-8">
        <button onClick={create} disabled={creating} data-testid="patients-new" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">
          <Plus size={18} /> {T.newPatient}
        </button>
      </div>

      {!loading && files.length > 0 && (
        <div className="max-w-md mx-auto mb-8 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={T.searchPlaceholder}
            data-testid="patients-search"
            className="w-full rounded-full border border-[var(--border)] bg-[var(--sand)] pl-11 pr-4 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--gold)]"
          />
        </div>
      )}

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7 mb-8" data-testid="patients-reminders">
        <h2 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{T.remindersTitle}</h2>
        {reminders.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)] italic">{T.noReminders}</p>
        ) : (
          <div className="space-y-3">
            {reminders.map((r) => (
              <div key={r.id} onClick={() => navigate(`/praticien/patients/${r.id}`)} data-testid={`reminder-${r.id}`}
                className="cursor-pointer rounded-2xl bg-white/70 border border-[var(--border)] p-4 hover:border-[var(--gold)] transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <span className="font-medium text-[var(--ink)]">{r.name || T.unnamed}</span>
                  {r.next_session && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-white bg-[var(--gold)] rounded-full px-3 py-1">
                      <Clock size={13} /> {T.nextSession} : {new Date(r.next_session).toLocaleString(lang === "es" ? "es-ES" : "fr-FR")}
                      {r.upcoming_count > 1 ? ` (+${r.upcoming_count - 1})` : ""}
                    </span>
                  )}
                </div>
                {r.priority_objectives && r.priority_objectives.length > 0 && (
                  <p className="text-sm text-[var(--ink-soft)]"><strong className="text-[#B0553B]">{T.priorityObjectives} :</strong> {r.priority_objectives.join(" · ")}</p>
                )}
                {r.next_objective && (
                  <p className="text-sm text-[var(--ink-soft)]"><strong>{T.nextObjective} :</strong> {r.next_objective}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-center text-[var(--ink-soft)] py-10" data-testid="patients-loading">{T.loading}</p>
      ) : files.length === 0 ? (
        <p className="text-center text-[var(--ink-soft)] italic py-10" data-testid="patients-empty">{T.listEmpty}</p>
      ) : (
        (() => {
          const q = query.trim().toLowerCase();
          const filtered = q
            ? files.filter((f) => `${f.prenom || ""} ${f.nom || ""}`.toLowerCase().includes(q))
            : files;
          if (filtered.length === 0) {
            return <p className="text-center text-[var(--ink-soft)] italic py-10" data-testid="patients-no-result">{T.noSearchResult}</p>;
          }
          return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="patients-list">
          {filtered.map((f) => {
            const name = [f.prenom, f.nom].filter(Boolean).join(" ") || T.unnamed;
            return (
              <div key={f.id} onClick={() => navigate(`/praticien/patients/${f.id}`)} data-testid={`patient-card-${f.id}`}
                className="group cursor-pointer flex items-center gap-4 rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-5 hover:shadow-md hover:border-[var(--gold)] transition-all">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[var(--gold)]/15 text-[var(--gold)] shrink-0"><User size={20} /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-serif-title text-xl text-[var(--ink)] group-hover:text-[var(--gold)] transition-colors truncate">{name}</p>
                  <p className="text-xs text-[var(--ink-soft)]">
                    {f.session_count} {T.sessionsCount} · {T.lastUpdate} {new Date(f.updated_at).toLocaleDateString(lang === "es" ? "es-ES" : "fr-FR")}
                  </p>
                </div>
                <button onClick={(e) => remove(e, f.id)} data-testid={`patient-delete-${f.id}`} className="text-[var(--ink-soft)] hover:text-[#B0553B] transition-colors shrink-0" aria-label={T.delete}>
                  <Trash2 size={18} />
                </button>
                <ArrowRight size={18} className="text-[var(--gold)] shrink-0" />
              </div>
            );
          })}
        </div>
          );
        })()
      )}
    </Layout>
  );
}
